// The rule for telling a barcode scan apart from someone typing.
//
// Kept free of the DOM so it can be tested directly. useHardwareScanner is the
// thin wrapper that feeds it keyboard events — the judgement lives here, where
// it can be pinned down by tests rather than by plugging in a scanner and
// hoping.

export interface ScanKeyEvent {
  /** KeyboardEvent.key */
  key: string;
  /** Timestamp in ms. */
  at: number;
  /** Whether a text field currently has focus. */
  typing: boolean;
}

export interface ScanReaderOptions {
  /**
   * Longest gap between characters that still counts as one burst. A person
   * cannot hold sub-120ms gaps; a wedge scanner is far quicker than that.
   */
  charGapMs?: number;
  /**
   * How long after the last character a terminator is still believed. Wider
   * than charGapMs on purpose: a DataWedge profile with a key-event delay can
   * leave a noticeable pause before the suffix, and a scan that arrived
   * perfectly well should not be thrown away for it.
   */
  staleMs?: number;
  /** Below this, it is a stray keypress rather than a label. */
  minLength?: number;
  /**
   * How long a burst must sit quiet before it counts as finished on its own,
   * with no terminator at all.
   *
   * Wider than charGapMs, or a burst would be cut in half at its first slow
   * character. This is what makes a scanner whose profile sends no ENTER
   * suffix work anyway: the characters are the scan, and the suffix was only
   * ever a convenient way to know the scan had ended.
   */
  settleMs?: number;
}

export interface ScanReader {
  /** Feed one key. Returns a completed code, or null. */
  push(e: ScanKeyEvent): string | null;
  /**
   * Ask whether the burst has stood quiet long enough to be a finished scan.
   * Called on a timer rather than on a key, because the whole point is that no
   * further key arrived. Returns a completed code, or null.
   */
  settle(e: Omit<ScanKeyEvent, 'key'>): string | null;
  /** Current buffer — for tests and diagnostics. */
  peek(): string;
}

/** Terminators a scanner may be configured to send. */
const TERMINATORS = new Set(['Enter', 'Tab']);

export function createScanReader(options: ScanReaderOptions = {}): ScanReader {
  const charGapMs = options.charGapMs ?? 120;
  const staleMs = options.staleMs ?? 1000;
  const minLength = options.minLength ?? 3;
  const settleMs = options.settleMs ?? 180;

  let buffer = '';
  let lastCharAt = 0;

  return {
    peek: () => buffer,

    settle({ at, typing }) {
      if (typing) return null;
      if (buffer.length < minLength) return null;
      if (at - lastCharAt < settleMs) return null;
      const code = buffer;
      buffer = '';
      return code;
    },

    push({ key, at, typing }) {
      if (TERMINATORS.has(key)) {
        const code = buffer;
        buffer = '';
        if (typing) return null;
        if (at - lastCharAt >= staleMs) return null;
        return code.length >= minLength ? code : null;
      }

      // Anything that is not a single character (Shift, F5, ArrowLeft) is not
      // part of a barcode and should not break the burst either.
      if (key.length !== 1) return null;

      if (at - lastCharAt > charGapMs) buffer = '';
      lastCharAt = at;
      buffer += key;
      return null;
    },
  };
}
